import * as THREE from 'three'

import BaseDimension from './baseDimension'
import MathsUtils from '@/utils/mathsUtils'
import GlassDesignerTypes from '../types'


// Angular Dimension
// Supports angular dimension built from two lines
export default class AngularDimension extends BaseDimension {
  constructor(editor, options = {}) {
    super(editor, options)

    this.dimType = options.dimType || GlassDesignerTypes.DimensionTypes.angularGlassSegmentDimension

    this.displayPoint = null      // Display point
    this.primaryDirection = new THREE.Vector3(0, 0, 0)
    this.angleDirection = new THREE.Vector3(0, 0, 0)
    this.labelDirection = null

    this.primaryLineLength = 0
    this.secondaryLineLength = 0

    this.textScalarMax = 2.0  // Maximum size to scale the text. Stops text getting too big when zoomed right out

    this.arrowLength = 6
    this.arrowWidth = 5
    this.arrowScalarMax = 2.75

    this.squareSize = 12.5
    this.squareScalar = 0.9
    this.squareScalarMax = 2.75

    this.arcRadius = 25
    this.arcScalar = 1.0
    this.arcScalarMax = 1.9

    this.textMinRadius = 20   // When zooming in, moves the text in for readability
    this.textMaxRadius = 55   // When zooming out, moves the text out for readability
    this.textRadius = (this.textMaxRadius + this.textMinRadius) / 2
    this.startAngle = 0
    this.endAngle = 0
    this.midAngle = 0   // Used for internal calculations
    this.angle = 0

    // Ratio to scale visibility of dimension size by zoom level
    this.autoHideEnabled = this.dimType == GlassDesignerTypes.DimensionTypes.angularGlassSegmentDimension
    this.minSizeVisibleFactor = 25.0
    this.minSizeVisibleMax = 75.0
    this.autoHideZoomMax = 7.75

    this.startDir = new THREE.Vector3()
    this.endDir = new THREE.Vector3()

    this.isClockwise = true
    this.isSquareAngle = false
    this.squareAngleDisabled = !!options.squareAngleDisabled

    this.squareAngleObj = null
    this.arcObj = null
    this.startArrowObj = null
    this.endArrowObj = null
    this.angleLabelObj = null    
    
    this.listenMouseEvents = false
  }

  // Reset the settings & state
  reset() {
    super.reset()

    this.textScalarMax = 2.0  // Maximum size to scale the text. Stops text getting too big when zoomed right out

    this.arrowLength = 6
    this.arrowWidth = 5
    this.arrowScalarMax = 2.75

    this.arcRadius = 25
    this.arcScalar = 1.0
    this.arcScalarMax = 2.0

    this.squareSize = 12.5
    this.squareScalar = 0.9
    this.squareScalarMax = 2.75

    // Ratio to scale visibility of dimension size by zoom level
    this.autoHideEnabled = this.dimType == GlassDesignerTypes.DimensionTypes.angularGlassSegmentDimension
    this.minSizeVisibleFactor = 25.0
    this.minSizeVisibleMax = 75.0
    this.autoHideZoomMax = 7.75

    this.textMinRadius = 20   // When zooming in, moves the text in for readability
    this.textMaxRadius = 55   // When zooming out, moves the text out for readability
    this.textRadius = (this.textMaxRadius + this.textMinRadius) / 2
    this.startAngle = 0
    this.endAngle = 0
    this.midAngle = 0   // Used for internal calculations
    this.isClockwise = true
    this.angle = 0
    this.isSquareAngle = false
  }

  // Get the angle for the angular dimension
  getAngle() {
    return this.angle
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    if(this.isReadOnly)
      return
    if(!this.editor.isSelectEnabled())
      return

    // If over dimension label, change to a hand
    this.labelList.forEach(obj => {

      if(mouseData.raycaster.intersectObject(obj, true).length > 0) {
        this.editor.setMouseCursorHand()
      }
    })
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData, intersectionInfo) {
    if(this.isReadOnly)
      return

    this.labelList.forEach((obj, index) => {
      if(obj.id == intersectionInfo.objectId) {

        this.editor.dimensionLabelClicked(this)
      }
    })      
  }


  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var scale = 1.0 / this.editor.camera.zoom
    let smallestLineLength = Math.min(this.primaryLineLength, this.secondaryLineLength)

    // Hide the dimension if it's too small unless we're zooming in
    // Prevents tiny dimensions cluttering the view when viewing the whole shape
    // Note: Don't set visibility on this BaseObject, only the THREE scene object otherwise onBeforeRender() will not be called again
    if(this.sceneObject && this.autoHideEnabled) {
      let minLength = Math.min(scale * this.minSizeVisibleFactor, this.minSizeVisibleMax)
      this.sceneObject.visible = smallestLineLength > minLength

      // If zoomed out too far, hide dimensions for performance
      if(this.sceneObject.visible) {
        if(scale > this.autoHideZoomMax)
          this.sceneObject.visible = false
      }
    }
    // Don't bother with the rest if hidden
    if(!this.sceneObject.visible)
      return


    if(this.squareAngleObj) {
      let scalarMax = this.squareScalarMax
      // If the smallest line associated with the square angle is <= 100mm then use 20% the max distance
      if(smallestLineLength <= 100) {
        scalarMax *= 0.20
      }

      var squareScale = Math.min(scale * this.squareScalar, scalarMax)
      this.squareAngleObj.scale.set(squareScale, squareScale, 1)
    }

    // Scale the regular angular dimension
    if(!this.isSquareAngle) {
      let scalarMax = this.arcScalarMax
      // If the smallest line associated with the square angle is <= 100mm then use 20% the max distance
      if(smallestLineLength <= 100) {
        scalarMax *= 0.20
      }

      var arcScale = Math.min(scale * this.arcScalar, scalarMax)
      if(this.arcObj) {
        this.arcObj.scale.set(arcScale, arcScale, 1)
      }

      let arrowRadius = this.arcRadius * arcScale
      if(this.startArrowObj)
        this.startArrowObj.position.set(this.displayPoint.x + this.startDir.x * arrowRadius, this.displayPoint.y + this.startDir.y * arrowRadius, 0)
      if(this.endArrowObj)
        this.endArrowObj.position.set(this.displayPoint.x + this.endDir.x * arrowRadius, this.displayPoint.y + this.endDir.y * arrowRadius, 0)

      if(this.angleLabelObj) {
        let textScalarMax = this.textScalarMax
        // If the smallest line associated with the square angle is <= 100mm then use 20% the max distance
        if(smallestLineLength <= 100) {
          textScalarMax *= 0.20
        }

        var labelScale = Math.min(scale * this.textScalar, textScalarMax)
        // Adjust text offset width if angle is horizontal vs vertical
        var textWidth = 22 * Math.abs(this.labelDirection.x) + 7
        var labelDistance = arrowRadius + (textWidth * labelScale)
        

        this.angleLabelObj.position.set(this.displayPoint.x + this.labelDirection.x * labelDistance, this.displayPoint.y + this.labelDirection.y * labelDistance, 0)
      }
    }
  }


  /**
   * Build the angular dimension from 2 lines
   * Automatically calculates directions
   * Assumes the endpoint of the 1st line connects to the startpoint of the 2nd line
   */
  buildFromLines(lineList, intersectionPoint, userFixed = false) {
    let userConfig = this.editor.getUserConfig()

    if(lineList.length != 2)
      return

    this.clearPoints()

    this._isUserFixed = userFixed

    // Assumes 2D directions
    this.displayPoint = intersectionPoint.clone()

    var startPoint = lineList[0].startPoint
    var endPoint = lineList[0].endPoint
    if(lineList[0].isFlipped) {
      startPoint = lineList[0].endPoint
      endPoint = lineList[0].startPoint
    }

    this.primaryDirection = endPoint.clone().sub(startPoint)
    this.primaryLineLength = this.primaryDirection.length()
    this.primaryDirection.normalize()

    this.angleDirection = lineList[1].endPoint.clone().sub(lineList[1].startPoint)
    this.secondaryLineLength = this.angleDirection.length()
    this.angleDirection.normalize()

    this.labelDirection = this.primaryDirection.clone()
    this.labelDirection.normalize()
    this.labelDirection.add(this.angleDirection)
    this.labelDirection.normalize()

    var primaryDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(this.primaryDirection)))
    var angleDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(this.angleDirection)))
    var labelDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(this.labelDirection)))

    this.startAngle = angleDirDegrees
    this.endAngle = primaryDirDegrees
    this.midAngle = labelDirDegrees
    this.isClockwise = true

    this.angle = MathsUtils.roundNumber(MathsUtils.calculateShortestAngleDistanceDegrees(this.endAngle, this.startAngle), userConfig.angularDimensionPrecision)

    let angleValueBetween = (value, min, max) => {
      return min <= value && max >= value
    }

    // Adjust / flip angle to show on the correct side
    if(this.startAngle > this.endAngle) {
      if(angleValueBetween(this.midAngle, this.endAngle, this.startAngle)) {
        let tmp = this.startAngle
        this.startAngle = this.endAngle
        this.endAngle = tmp
        this.isClockwise = false
      } else {
        this.endAngle += 360
      }
    } else {
      if(!angleValueBetween(this.midAngle, this.startAngle, this.endAngle)) {
        this.startAngle = this.endAngle
        this.endAngle = this.startAngle + this.angle
        this.isClockwise = false
      }
    }

    // Don't show anything if 0 or 180 degrees
    if(MathsUtils.roundNumber(Math.abs(this.angle - 180.0), userConfig.angularDimensionPrecision) == 0 || MathsUtils.roundNumber(Math.abs(this.angle), userConfig.angularDimensionPrecision) == 0)
      return

    this.build()
  }


  // Build & create all the objects
  build() {
    let userConfig = this.editor.getUserConfig()

    // Adjust properties
    this.listenMouseEvents = !this.isReadOnly

    let displayAngle = MathsUtils.roundNumber(this.angle, userConfig.angularDimensionPrecision)

    let lineColour = this.editor.getTheme().dimLineColour
    let labelColour = this.editor.getTheme().dimLabelColour
    let fixedLabelColour = this.editor.getTheme().dimFixedLabelColour
    let lineOpacity = this.editor.getTheme().dimLineOpacity
    let squareColour = this.editor.getTheme().dimSquareColour

    var opacityScalar = 1.0

    // Dim for shape editing mode
    if(this.editor.isShapeEditingMode())
      opacityScalar = 0.33

    var arcStart = this.startAngle
    var arcEnd = this.endAngle
    
    let startAngleRads = MathsUtils.degreesToRadians(this.startAngle)
    let endAngleRads = MathsUtils.degreesToRadians(this.endAngle)

    this.startDir = MathsUtils.generate3DDirectionVector(startAngleRads)
    this.endDir = MathsUtils.generate3DDirectionVector(endAngleRads)

    this.isSquareAngle = MathsUtils.roundNumber(Math.abs(this.angle - 90), userConfig.angularDimensionPrecision) == 0 && !this.squareAngleDisabled

    // Different shape for 90deg right angle
    if(this.isSquareAngle) {
      let sqStartPnt = new THREE.Vector3(this.startDir.x * this.squareSize, this.startDir.y * this.squareSize)
      let sqMidPoint = sqStartPnt.clone().addScaledVector(this.endDir, this.squareSize)
      let sqEndPoint = new THREE.Vector3(this.endDir.x * this.squareSize, this.endDir.y * this.squareSize)

      if(!this.squareAngleObj)
        this.squareAngleObj = this.createDimensionSquaredAngle(this.displayPoint, sqStartPnt, sqMidPoint, sqEndPoint, squareColour)
      else
        this.updateDimensionSquaredAngle(this.squareAngleObj, this.displayPoint, sqStartPnt, sqMidPoint, sqEndPoint, squareColour)

    } else {
      // Create dimension arc
      if(!this.arcObj)
        this.arcObj = this.createDimensionArc(this.displayPoint, this.arcRadius, arcStart, arcEnd, lineColour, lineOpacity * opacityScalar)
      else
        this.updateDimensionArc(this.arcObj, this.displayPoint, this.arcRadius, arcStart, arcEnd, lineColour, lineOpacity * opacityScalar)

      // Create start/end arrows
      if(!this.startArrowObj)
        this.startArrowObj = this.createArrow()

      this.startArrowObj.position = this.displayPoint.clone().addScaledVector(this.startDir, this.arcRadius)
      this.startArrowObj.rotation.z = startAngleRads + (Math.PI/2)

      if(!this.endArrowObj)
        this.endArrowObj = this.createArrow()

      this.endArrowObj.position = this.displayPoint.clone().addScaledVector(this.endDir, this.arcRadius)
      this.endArrowObj.rotation.z = endAngleRads - (Math.PI/2)

    
      // Create label
      let labelPosition = this.displayPoint.clone().addScaledVector(this.labelDirection, this.textRadius)
      let labelText = this.calculateAngularLabelText(displayAngle)

      if(!this.angleLabelObj)
        this.angleLabelObj = this.createDimensionLabel(labelPosition, labelText)
      
      this.angleLabelObj.position.set(labelPosition.x, labelPosition.y, labelPosition.z)
      this.angleLabelObj.anchorX = 'center'
      this.angleLabelObj.anchorY = 'middle'
      this.angleLabelObj.text = labelText
      this.angleLabelObj.color = this._isUserFixed ? fixedLabelColour : labelColour

      if(this.isReadOnly) {
        this.angleLabelObj.color = labelColour
        this.angleLabelObj.material.opacity = this.editor.getTheme().dimReadOnlyOpacity
      } else {
        this.angleLabelObj.material.opacity = 1.0
      }

      this.angleLabelObj.material.opacity *= opacityScalar
    }

    // Hide/Show objects based on squared angle or not
    if(this.squareAngleObj)
      this.squareAngleObj.visible = this.isSquareAngle

    if(this.arcObj)
      this.arcObj.visible = !this.isSquareAngle
    if(this.startArrowObj)
      this.startArrowObj.visible = !this.isSquareAngle
    if(this.endArrowObj)
      this.endArrowObj.visible = !this.isSquareAngle
    if(this.angleLabelObj)
      this.angleLabelObj.visible = !this.isSquareAngle
  }



  

}
